// src/components/dashboard/CarbonRemovalTechnologies.js
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useCdrBuckets } from "../../hooks/useCdrBuckets";

export default function CarbonRemovalTechnologies({
  scenario,
  comparisonScenarioKey = null,
  from = 2020,
  to = 2050,
  step = 5,
  includePrimaryTotal = false, // controls primary only
  unitScale = 1,
  className = "w-full h-96 m-auto",
  title = "Carbon Removal Technologies Projection (MtCO₂/yr)",
}) {
  const primary = useCdrBuckets({
    scenario,
    from,
    to,
    step,
    unitScale,
    includeTotalInPrimary: includePrimaryTotal,
  });

  const comparison = useCdrBuckets({
    scenario: comparisonScenarioKey || undefined,
    from,
    to,
    step,
    unitScale,
    includeTotalInPrimary: false, // we’ll overlay it separately
  });

  const labels = primary.labels?.length ? primary.labels : comparison.labels;

  const alignToLabels = (years = [], values = []) => {
    const map = new Map(years.map((y, i) => [String(y), values[i] ?? 0]));
    return (labels || []).map((y) => map.get(String(y)) ?? 0);
  };

  // Single dashed comparison total, not stacked
  const comparisonTotalDataset = useMemo(() => {
    if (!comparisonScenarioKey || !comparison?.labels?.length || !labels?.length) return null;
    return {
      ...comparison.totalDataset,
      label: "Total (comp)",
      data: alignToLabels(comparison.labels, comparison.totalDataset.data),
      yAxisID: "y2",
      borderDash: [6, 4],
      borderWidth: 2,
      pointRadius: 0,
      fill: false,
      backgroundColor: undefined,
    };
  }, [comparisonScenarioKey, comparison.labels, comparison.totalDataset, labels]);

  const datasets = useMemo(() => {
    return comparisonTotalDataset
      ? [...(primary.datasets || []), comparisonTotalDataset]
      : (primary.datasets || []);
  }, [primary.datasets, comparisonTotalDataset]);

  // Sync y ranges so the overlay makes sense
  const scaleMax = useMemo(() => {
    const primaryStackTotals =
      labels?.map((_, i) =>
        (primary.datasets || [])
          .filter((ds) => !ds.isTotal)
          .reduce((sum, ds) => sum + (Number(ds.data?.[i]) || 0), 0)
      ) || [];
    const maxPrimary = Math.max(0, ...primaryStackTotals);
    const maxComp = Math.max(0, ...(comparisonTotalDataset?.data || []));
    const raw = Math.max(maxPrimary, maxComp);
    if (!isFinite(raw) || raw <= 0) return undefined;
    const stepSz = raw > 200 ? 20 : raw > 100 ? 10 : raw > 50 ? 5 : 2;
    return Math.ceil(raw / stepSz) * stepSz;
  }, [labels, primary.datasets, comparisonTotalDataset]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true, padding: 12 } },
      title: { display: true, text: title },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed?.y ?? 0;
            return `${ctx.dataset.label}: ${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} Mt`;
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" } },
      y: {
        min: 0,
        max: 120,
        stacked: true,
        beginAtZero: true,
        title: { display: true, text: "MtCO₂ removed per year" },
        suggestedMax: scaleMax,
      },
      y2: {
        min: 0,
        max: 120,
        position: "right",
        stacked: false,
        beginAtZero: true,
        grid: { display: false },
        ticks: { display: false },
        suggestedMax: scaleMax,
      },
    },
  };

  const isEmpty =
    !labels?.length ||
    !datasets?.length ||
    datasets.every((d) => (d.data || []).every((v) => v === 0));

  return (
    <div className={className}>
      {isEmpty ? (
        <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">
          No CO₂ removals data for the selected scenario/years.
        </div>
      ) : (
        <Line data={{ labels, datasets }} options={options} />
      )}
    </div>
  );
}
